// Copyright ©️ 2026 eVolve MEP, LLC

import { type ComponentType, useEffect, useRef } from 'react';

import { Box, Button, Text, UnstyledButton } from '@mantine/core';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { isNotNil } from 'helpers/isNotNil';
import { useBendCart } from 'modules/Shop/EvolveFabrication/BendCreator/Cart/useBendCart';
import {
  BEND_TYPE_STEP,
  type BendCreatorStepIndex,
  bendCreatorStepIndexes,
  bendCreatorSteps,
  DIMENSIONS_STEP,
  nextStepOf,
  REVIEW_STEP,
  MATERIAL_STEP,
} from 'modules/Shop/EvolveFabrication/BendCreator/Nav/bendCreatorSteps';
import classes from 'modules/Shop/EvolveFabrication/BendCreator/Nav/NavPanel.module.css';
import { BendTypeStep } from 'modules/Shop/EvolveFabrication/BendCreator/Nav/steps/BendTypeStep';
import { DimensionsStep } from 'modules/Shop/EvolveFabrication/BendCreator/Nav/steps/DimensionsStep';
import { MaterialStep } from 'modules/Shop/EvolveFabrication/BendCreator/Nav/steps/MaterialStep';
import { ReviewStep } from 'modules/Shop/EvolveFabrication/BendCreator/Nav/steps/ReviewStep';
import { useAnimatedPaneHeight } from 'modules/Shop/EvolveFabrication/BendCreator/Nav/useAnimatedPaneHeight';
import { type StepStatus, useStepStatuses } from 'modules/Shop/EvolveFabrication/BendCreator/Nav/useStepStatuses';
import { useAddToBendList } from 'modules/Shop/EvolveFabrication/BendCreator/State/useAddToBendList';
import { useBendBuilder } from 'modules/Shop/EvolveFabrication/BendCreator/State/useBendBuilder';
import { useCopyEditing } from 'modules/Shop/EvolveFabrication/BendCreator/State/useCopyEditing';

type StepRowProps = {
  step: BendCreatorStepIndex;
  status: StepStatus;
  onOpen: (step: BendCreatorStepIndex) => void;
};

const StepCircle = ({ step, status }: Pick<StepRowProps, 'step' | 'status'>) => {
  if (status.hasError) {
    return (
      <Box className={classes.stepCircle} data-error aria-label="Has an error">
        !
      </Box>
    );
  }
  if (status.done) {
    return (
      <Box className={classes.stepCircle} data-done aria-label="Done">
        <EvolveIcon icon="Check" size="sm" color="white" />
      </Box>
    );
  }
  return <Box className={classes.stepCircle}>{step + 1}</Box>;
};

const StepRow = ({ step, status, onOpen }: StepRowProps) => {
  const { name } = bendCreatorSteps[step];
  const showValue = status.done && isNotNil(status.value);
  return (
    <UnstyledButton className={classes.stepRow} onClick={() => onOpen(step)}>
      <StepCircle step={step} status={status} />
      <Box className={classes.stepText}>
        {showValue ? (
          <>
            <Text component="span" className={classes.stepName}>
              {name}
            </Text>
            <Text component="span" className={classes.stepValue}>
              {status.value}
            </Text>
          </>
        ) : (
          <Text component="span" className={classes.stepTitle}>
            {name}
          </Text>
        )}
      </Box>
      <EvolveIcon icon="ArrowRight" size="sm" className={classes.chevron} />
    </UnstyledButton>
  );
};

const stepContent: Record<BendCreatorStepIndex, ComponentType> = {
  [MATERIAL_STEP]: MaterialStep,
  [BEND_TYPE_STEP]: BendTypeStep,
  [DIMENSIONS_STEP]: DimensionsStep,
  [REVIEW_STEP]: ReviewStep,
};

const StepFooter = ({ step }: { step: BendCreatorStepIndex }) => {
  const { goToNextStep } = useBendBuilder();
  const { canAdd, addToBendList } = useAddToBendList();
  const { bends, cartOpen } = useBendCart();
  const { editingBendId, status, finishEditing, cancelEditing } = useCopyEditing();

  // Editing a listed bend: changes already went to the list, so there is no Next; move between steps from its row.
  if (isNotNil(editingBendId)) {
    return (
      <Box className={classes.stepFooter}>
        <Button className={classes.footerButton} variant="default" onClick={cancelEditing}>
          Cancel
        </Button>
        <Button
          className={classes.footerButton}
          disabled={status !== 'valid'}
          onClick={finishEditing}
          leftSection={<EvolveIcon icon="Check" size="sm" />}
        >
          Done
        </Button>
      </Box>
    );
  }

  if (step === REVIEW_STEP) {
    return (
      <Box className={classes.stepFooter}>
        <Button
          className={classes.footerButton}
          // With the bend list open its Add to work order is the primary action, so this one steps down.
          variant={cartOpen && bends.length > 0 ? 'outline' : 'filled'}
          disabled={!canAdd}
          onClick={addToBendList}
          leftSection={<EvolveIcon icon="Add" size="sm" />}
        >
          Add to bend list
        </Button>
      </Box>
    );
  }
  const nextStep = nextStepOf[step];
  if (nextStep === null) return null;
  return (
    <Box className={classes.stepFooter}>
      <Button
        className={classes.footerButton}
        onClick={goToNextStep}
        rightSection={<EvolveIcon icon="ArrowRight" size="xs" />}
      >
        Next: {bendCreatorSteps[nextStep].buttonName}
      </Button>
    </Box>
  );
};

/**
 * The Bend Creator's left panel. Its root lists the four steps; tapping one pushes that step in over the list,
 * with a back button that always returns to the list.
 */
export const NavPanel = () => {
  const { nav, currentStep, openStep, openRoot } = useBendBuilder();
  const { bends, clearPreview } = useBendCart();
  const { editingBendId, finishEditing } = useCopyEditing();
  const statuses = useStepStatuses();
  const editingIndex = bends.findIndex(({ cartBendId }) => cartBendId === editingBendId);
  const editingBend = bends[editingIndex];

  // A newly opened step starts at the top; the same step keeps its scroll position.
  const stepBodyRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    stepBodyRef.current?.scrollTo({ top: 0 });
  }, [/* effect dep */ currentStep, /* effect dep */ editingBendId]);

  // Opening a step works on the bend being built, so the model goes back to showing it.
  const openBuilderStep = (step: BendCreatorStepIndex) => {
    clearPreview();
    openStep(step);
  };
  const CurrentStepContent = stepContent[currentStep];

  // The panel's height follows its content, and animates when the list and a step swap, or the step changes.
  const trackRef = useRef<HTMLDivElement>(null);
  useAnimatedPaneHeight(trackRef, `${nav}:${String(currentStep)}:${editingBendId ?? ''}`);

  return (
    <Box ref={trackRef} className={classes.track}>
      <Box
        className={`${classes.pane} ${classes.rootPane}`}
        data-hidden={nav !== 'root' || undefined}
        inert={nav !== 'root'}
      >
        {bendCreatorStepIndexes.map((step) => (
          <StepRow key={step} step={step} status={statuses[step]} onOpen={openBuilderStep} />
        ))}
      </Box>
      <Box
        className={`${classes.pane} ${classes.stepPane}`}
        data-hidden={nav !== 'step' || undefined}
        inert={nav !== 'step'}
      >
        <Box className={classes.stepHeader}>
          <UnstyledButton
            className={classes.backButton}
            // Back while editing a listed bend keeps the changes, like Done.
            onClick={isNotNil(editingBendId) ? finishEditing : openRoot}
            aria-label="Back to steps"
          >
            <EvolveIcon icon="ArrowLeft" size="sm" />
          </UnstyledButton>
          <Box className={classes.headerText}>
            {isNotNil(editingBend) ? (
              <Text component="span" className={classes.eyebrow} data-editing>
                Editing #{editingIndex + 1} · {editingBend.values.partName}
              </Text>
            ) : (
              <Text component="span" className={classes.eyebrow}>
                Step {currentStep + 1} of {bendCreatorSteps.length}
              </Text>
            )}
            <Text component="span" className={classes.headerTitle}>
              {bendCreatorSteps[currentStep].name}
            </Text>
          </Box>
        </Box>
        <Box ref={stepBodyRef} className={classes.stepBody}>
          <Text className={classes.guidance}>
            {isNotNil(editingBend)
              ? `Changes go straight to #${editingIndex + 1} in the list. Cancel puts it back as it was.`
              : bendCreatorSteps[currentStep].guidance}
          </Text>
          <CurrentStepContent />
        </Box>
        <StepFooter step={currentStep} />
      </Box>
    </Box>
  );
};
